import {useCallback, useMemo, useState} from 'react';

import {assert} from 'Asserts';
import {ProcedureRunDataEntryMode, ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ExperimentalInputs,
  ExperimentalInputsContextProperties,
} from 'procedures/planning/ProcedurePlanTypes';
import {
  Condition,
  useExperimentalInputsContextProperties,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';

import {StudyDesignTabEditState} from '../design-tabs/StudyDesignTabSavingButtons';
import {
  MethodExecutionLimitState,
  useMethodExecutionLimitState,
} from '../execution-status/useMethodExecutionLimitState';
import {ExperimentFlowchart, LargestTableTypeSizes} from './ExperimentalInputs';
import {PlanConditionsByMethodId, withMethodConditions, withSharedConditions} from './planConditionState';
import {getSharedConditionRows} from './sharedPlanConditionView';
import {StudyDesignFlowchartVersion} from './useStudyDesignState';
import {StudyDesignUiState, useStudyDesignUiState} from './useStudyDesignUiState';

interface UsePlanStudyDesignStateParams {
  initialExperimentalInputs: ExperimentalInputs;
  initialPlanConditions: PlanConditionsByMethodId;
  templateFlowchart: ExperimentFlowchart;
  procedureType: ProcedureType;
  flowchartVersion: StudyDesignFlowchartVersion;
  dataEntryMode: ProcedureRunDataEntryMode;
  procedureId: string;
  selectFirstMethodInitially?: boolean;
}

export interface UsePlanStudyDesignStateResult extends StudyDesignUiState {
  planConditionsByMethodId: PlanConditionsByMethodId;
  setConditionsForMethod: (
    methodId: string,
    nextRows: Array<Condition>,
    prevIndexes: Array<number | null>
  ) => void;
  experimentalInputs: ExperimentalInputs;
  setExperimentalInputs: (inputs: ExperimentalInputs) => void;
  /**
   * Writes a combined-table edit onto every method, keeping each method's id.
   */
  setSharedConditions: (rows: Array<Condition>, prevIndexes: Array<number | null>) => void;
  /** Uniform combined-table rows. Null when methods disagree on name, description, or replicate count. */
  uniformPlanRows: Array<Condition> | null;
  context: ExperimentalInputsContextProperties;
  methodExecutionLimitState: MethodExecutionLimitState;
  templateFlowchart: ExperimentFlowchart;
}

interface PlanConditionsState {
  planConditionsByMethodId: PlanConditionsByMethodId;
  experimentalInputs: ExperimentalInputs;
  procedureId: string;
}

/**
 * Study-design state for a procedure run plan. Condition rows are stored per flowchart method.
 */
export function usePlanStudyDesignState({
  initialExperimentalInputs,
  initialPlanConditions,
  templateFlowchart,
  procedureType,
  flowchartVersion,
  dataEntryMode,
  procedureId,
  selectFirstMethodInitially = true,
}: UsePlanStudyDesignStateParams): UsePlanStudyDesignStateResult {
  const uiState = useStudyDesignUiState({
    templateFlowchart,
    selectFirstMethodInitially,
    initialEditMode: true,
  });
  const {editState} = uiState;

  // Condition rows and experimental inputs, stored per flowchart method. Reset when the procedure changes.
  const [data, setData] = useState<PlanConditionsState>({
    planConditionsByMethodId: initialPlanConditions,
    experimentalInputs: initialExperimentalInputs,
    procedureId,
  });
  const stored = useMemo(
    () =>
      data.procedureId === procedureId
        ? data
        : {
            planConditionsByMethodId: initialPlanConditions,
            experimentalInputs: initialExperimentalInputs,
            procedureId,
          },
    [data, initialExperimentalInputs, initialPlanConditions, procedureId]
  );
  if (stored !== data) {
    setData(stored);
  }

  const setConditionsForMethod = useCallback(
    (methodId: string, nextRows: Array<Condition>, prevIndexes: Array<number | null>): void => {
      setData({
        procedureId,
        ...withMethodConditions(
          stored.planConditionsByMethodId,
          stored.experimentalInputs,
          methodId,
          nextRows,
          prevIndexes
        ),
      });
    },
    [procedureId, stored]
  );

  const setSharedConditions = useCallback(
    (rows: Array<Condition>, prevIndexes: Array<number | null>): void => {
      setData({
        procedureId,
        ...withSharedConditions(
          stored.planConditionsByMethodId,
          stored.experimentalInputs,
          rows,
          prevIndexes
        ),
      });
    },
    [procedureId, stored]
  );

  const setExperimentalInputs = useCallback(
    (experimentalInputs: ExperimentalInputs): void => {
      setData({...stored, experimentalInputs, procedureId});
    },
    [procedureId, stored]
  );

  const {planConditionsByMethodId, experimentalInputs} = stored;

  const uniformPlanRows = useMemo(
    () => getSharedConditionRows(planConditionsByMethodId),
    [planConditionsByMethodId]
  );
  // Limit helpers expect one table. Uniform plans report their shared rows; non-uniform plans are
  // read-only in this flow and report no rows. Per-method limits arrive with non-uniform editing.
  const conditionsForExecutionLimits = uniformPlanRows ?? [];

  const context = useExperimentalInputsContextProperties(
    planConditionsByMethodId,
    experimentalInputs,
    setExperimentalInputs,
    templateFlowchart,
    procedureType,
    editState !== StudyDesignTabEditState.EDITING,
    true
  );
  assert(context != null, 'Expected context to be non-null');

  const methodExecutionLimitState = useMethodExecutionLimitState(
    experimentalInputs,
    conditionsForExecutionLimits,
    flowchartVersion.largestTableTypeSizes as LargestTableTypeSizes,
    dataEntryMode
  );

  return {
    ...uiState,
    planConditionsByMethodId,
    setConditionsForMethod,
    setSharedConditions,
    uniformPlanRows,
    experimentalInputs,
    setExperimentalInputs,
    context,
    methodExecutionLimitState,
    templateFlowchart,
  };
}
